import { fetchAvailability } from '@/actions/fetch-availability';
import { TMonth } from '@/types';
import { useEffect, useMemo, useState } from 'react';
import { DateObject } from 'react-multi-date-picker';
import { months as allMonths } from '@/config/constants';
import { findAvailableTours } from '@/actions/find-available-tours';
import { useRouter } from 'next/navigation';

export type TAvailableMonths = TMonth & {
  isAvailable: boolean;
};

type TDay = {
  [key: number]: boolean;
};

export type TAvailability = {
  [year: number]: {
    [month: number]: TDay;
  };
};

const today = new Date();

interface IUseReservationProps {
  tourId: string;
  yearMonth: {
    [key: string]: Array<number>;
  };
  handleClose: () => void;
}

export const useReservation = ({ tourId, yearMonth, handleClose }: IUseReservationProps) => {
  const router = useRouter();
  const [yearIndex, setYearIndex] = useState<number>(0);
  const [adultCount, setAdultCount] = useState<number>(0);
  const [childrenCount, setChildrenCount] = useState<number>(0);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [months, setMonths] = useState<Array<TAvailableMonths>>([]);
  const [year, setYear] = useState<number>(0);
  const [calendarValue, setCalendarValue] = useState<DateObject | null>(null);
  const [availability, setAvailability] = useState<TAvailability>({});
  const [isDaySelected, setIsDaySelected] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(1);

  const resetForm = () => {
    setYearIndex(0);
    setAdultCount(0);
    setChildrenCount(0);
    setSelectedMonth(null);
    setCalendarValue(null);
    setAvailability({});
    setIsDaySelected(false);
    setTab(1);
    router.replace(location.pathname);
  };

  const availableYears = useMemo(() => {
    if (!yearMonth) return [];
    return Object.keys(yearMonth)
      .map(Number)
      .sort((a, b) => a - b);
  }, [yearMonth]);

  useEffect(() => {
    setYear(availableYears[yearIndex]);
    setCalendarValue(new DateObject(today).setYear(availableYears[yearIndex]));
  }, [availableYears, yearIndex]);

  const incrementAdults = () => setAdultCount((prevValue) => prevValue + 1);
  const decrementAdults = () => setAdultCount((prevValue) => (prevValue > 0 ? prevValue - 1 : 0));
  const incrementChildren = () => setChildrenCount((prevValue) => prevValue + 1);
  const decrementChildren = () => setChildrenCount((prevValue) => (prevValue > 0 ? prevValue - 1 : 0));
  const incrementYear = () => {
    const hasMultipleYears = availableYears.length > 1;
    if (!hasMultipleYears) return;
    setSelectedMonth(null);
    setYearIndex((prevIndex) => {
      if (prevIndex + 1 >= availableYears.length) return 0;
      return prevIndex + 1;
    });
  };
  const decrementYear = () => {
    const hasMultipleYears = availableYears.length > 1;
    if (!hasMultipleYears) return;
    setSelectedMonth(null);
    setYearIndex((prevIndex) => {
      if (prevIndex - 1 < 0) return availableYears.length - 1;
      return prevIndex - 1;
    });
  };

  const handleChangeMonth = async (month: number) => {
    const dateObject = new DateObject();
    dateObject.setYear(year);
    dateObject.setMonth(month + 1);
    setIsDaySelected(false);
    setCalendarValue(dateObject);
    if (availability[year] && availability[year][month]) {
      setSelectedMonth(month);
      return;
    }

    try {
      const dayTimes = await fetchAvailability({ tourId, adults: adultCount, children: childrenCount, month, year });
      const availableDays: { [key: number]: boolean } = {};
      dayTimes.forEach(({ day }) => (availableDays[day] = true));
      setSelectedMonth(month);
      setAvailability((prevValue) => ({
        ...prevValue,
        [year]: {
          ...prevValue[year],
          [month]: availableDays,
        },
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeCalendarValue = (value: DateObject) => {
    setIsDaySelected(true);
    setCalendarValue(value);
  };

  const mapMonths = (months: Set<number>) => {
    return allMonths.map((month) => ({ ...month, isAvailable: months.has(month.value) }));
  };

  useEffect(() => {
    const months = new Set(yearMonth[year]);
    if (!months.size) return;
    setMonths(() => mapMonths(months));
  }, [yearMonth, year]);

  const handleSubmit = (isMobile?: boolean) => {
    if (!year || selectedMonth == null || !calendarValue || !isDaySelected) return;
    const params = new URLSearchParams();
    params.append('year', year.toString());
    params.append('month', selectedMonth.toString());
    const { day } = new DateObject(calendarValue).subtract(1, 'day');
    params.append('day', day.toString());
    params.append('adults', adultCount.toString());
    params.append('children', childrenCount.toString());

    findAvailableTours(tourId, params.toString());
    if (isMobile) {
      setTab(2);
    } else {
      handleClose();
      setIsDaySelected(false);
    }
  };

  return {
    adultCount,
    incrementAdults,
    decrementAdults,
    childrenCount,
    incrementChildren,
    decrementChildren,
    year,
    incrementYear,
    decrementYear,
    months,
    selectedMonth,
    availability,
    calendarValue,
    isDaySelected,
    handleSubmit,
    handleChangeMonth,
    handleChangeCalendarValue,
    tab,
    resetForm,
  };
};
